import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Lock, LogOut, ShieldCheck } from "lucide-react";
import { NavLink } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminGate({ children }) {
  const [state, setState] = useState("checking"); // checking | out | otp | in
  const [pendingIdentifier, setPendingIdentifier] = useState("");
  const [pendingChannel, setPendingChannel] = useState("email");
  const [pendingDest, setPendingDest] = useState("");
  const [email, setEmail] = useState("");
  const [form, setForm] = useState({ identifier: "", password: "", website: "" });
  const [code, setCode] = useState("");
  const [otpError, setOtpError] = useState("");
  const openedAt = useRef(Date.now());
  const [busy, setBusy] = useState(false);

  const check = async () => {
    try {
      const r = await fetch(`${API}/auth/me`, { credentials: "include" });
      if (r.ok) {
        const u = await r.json();
        setEmail(u.email || "");
        setState("in");
      } else {
        setState("out");
      }
    } catch {
      setState("out");
    }
  };

  useEffect(() => {
    check();
  }, []);

  const login = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await fetch(`${API}/auth/login`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          identifier: form.identifier,
          password: form.password,
          website: form.website,
          elapsed_ms: Date.now() - openedAt.current,
        }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        toast.error(typeof data.detail === "string" ? data.detail : "Login failed");
        return;
      }
      if (data.status === "otp_sent") {
        setPendingIdentifier(form.identifier);
        setPendingChannel(data.channel || "email");
        setPendingDest(data.destination || "");
        setOtpError("");
        setState("otp");
      } else {
        toast.error("Unexpected response — try again");
      }
    } catch {
      toast.error("Login failed — check your connection");
    } finally {
      setBusy(false);
    }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await fetch(`${API}/auth/verify-otp`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          identifier: pendingIdentifier,
          code: code.replace(/\D/g, ""),
          website: form.website,
          elapsed_ms: Date.now() - openedAt.current,
        }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        setOtpError(typeof data.detail === "string" ? data.detail : "That code isn't right");
        return;
      }
      setEmail(data.email || pendingIdentifier);
      setState("in");
      toast.success("Welcome back!");
    } catch {
      setOtpError("Could not verify — check your connection");
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/auth/login`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          identifier: pendingIdentifier,
          password: form.password,
          website: form.website,
          elapsed_ms: Date.now() - openedAt.current,
        }),
      });
      const data = await r.json().catch(() => ({}));
      if (r.ok && data.status === "otp_sent") {
        setPendingChannel(data.channel || "email");
        setPendingDest(data.destination || "");
        setOtpError("");
        toast.success("A fresh code is on its way");
      } else {
        toast.error(typeof data.detail === "string" ? data.detail : "Could not resend — sign in again");
      }
    } catch {
      toast.error("Could not resend — check your connection");
    } finally {
      setBusy(false);
    }
  };

  const resetToCredentials = () => {
    setState("out");
    setCode("");
    setOtpError("");
    openedAt.current = Date.now();
  };

  const logout = async () => {
    await fetch(`${API}/auth/logout`, { method: "POST", credentials: "include" }).catch(() => {});
    setState("out");
    setForm({ identifier: "", password: "", website: "" });
    setCode("");
    openedAt.current = Date.now();
  };

  if (state === "checking") {
    return (
      <div className="flex min-h-[60vh] items-center justify-center text-[#241c0d]" data-testid="admin-gate-checking">
        <Loader2 size={28} className="animate-spin" />
      </div>
    );
  }

  if (state === "otp") {
    return (
      <div className="flex min-h-[70vh] items-center justify-center p-6 py-16" data-testid="admin-otp-portal">
        <form onSubmit={verifyOtp} className="card-dark w-full max-w-sm p-8" data-testid="admin-otp-form">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-[#f8b818]/15 text-[#f8b818]">
            <ShieldCheck size={24} />
          </span>
          <p className="font-display mt-4 text-3xl text-[#f8b818]" data-testid="admin-otp-channel-title">
            {pendingChannel === "sms" ? "CHECK YOUR PHONE" : "CHECK YOUR EMAIL"}
          </p>
          <p className="mt-2 text-sm text-[#b9b4a6]" data-testid="admin-otp-channel-note">
            {pendingChannel === "sms" ? (
              <>
                We texted a 6-digit code to <b className="text-[#faf5ea]">{pendingDest}</b>. Enter it below to
                finish signing in — it expires in 10 minutes.
              </>
            ) : (
              <>
                We emailed a 6-digit code to <b className="text-[#faf5ea]">{pendingDest}</b>. Enter it below to
                finish signing in — it expires in 10 minutes.
              </>
            )}
          </p>
          <input
            className="input-dark mt-6 text-center !text-xl !tracking-[10px]"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="······"
            required
            maxLength={6}
            value={code}
            onChange={(e) => {
              setCode(e.target.value.replace(/\D/g, ""));
              setOtpError("");
            }}
            data-testid="admin-otp-input"
          />
          {otpError && (
            <p className="mt-3 rounded-xl bg-[#d63a2f]/15 p-3 text-xs font-bold text-[#ff8a80]" data-testid="admin-otp-error">
              {otpError}
            </p>
          )}
          <button className="btn-gold mt-5 w-full justify-center" disabled={busy || code.length !== 6} data-testid="admin-otp-verify-btn">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <ShieldCheck size={15} />}
            {busy ? "Verifying…" : "Verify & sign in"}
          </button>
          <div className="mt-4 flex items-center justify-between text-[11px] font-extrabold uppercase tracking-widest">
            <button type="button" onClick={resend} className="text-[#f8b818] underline underline-offset-2" data-testid="admin-otp-resend-btn">
              Resend code
            </button>
            <button type="button" onClick={resetToCredentials} className="text-[#8a7b5e] underline underline-offset-2" data-testid="admin-otp-back-btn">
              Use a different account
            </button>
          </div>
        </form>
      </div>
    );
  }

  if (state === "out") {
    return (
      <div className="flex min-h-[70vh] items-center justify-center p-6 py-16" data-testid="admin-login-portal">
        <form onSubmit={login} className="card-dark w-full max-w-sm p-8" data-testid="admin-login-form">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-[#f8b818]/15 text-[#f8b818]">
            <Lock size={24} />
          </span>
          <p className="font-display mt-4 text-3xl text-[#f8b818]">ADMIN PORTAL</p>
          <p className="mt-2 text-sm font-bold text-[#241c0d]">
            For Cool Runnings staff only. Sign in with your mobile number and password — we'll text you a code
            to verify it's you. (Your email address works too.)
          </p>
          <input
            className="input-dark mt-6"
            type="tel"
            placeholder="+27 82 901 1111"
            required
            maxLength={32}
            autoComplete="username"
            value={form.identifier}
            onChange={(e) => setForm({ ...form, identifier: e.target.value })}
            data-testid="admin-phone-input"
          />
          <input
            className="input-dark mt-3"
            type="password"
            placeholder="Password"
            required
            maxLength={128}
            autoComplete="current-password"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            data-testid="admin-password-input"
          />
          <input
            type="text"
            name="website"
            value={form.website}
            onChange={(e) => setForm({ ...form, website: e.target.value })}
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            className="absolute -left-[9999px] h-0 w-0 opacity-0"
            data-testid="admin-website-hp"
          />
          <button className="btn-gold mt-5 w-full justify-center" disabled={busy} data-testid="admin-login-btn">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Lock size={15} />}
            {busy ? "Signing in…" : "SMS me a code"}
          </button>
          <p className="mt-4 text-center text-[11px] font-bold leading-relaxed text-[#241c0d]">
            Protected area. Repeated failed attempts lock this device out temporarily.
          </p>
        </form>
      </div>
    );
  }

  return (
    <div data-testid="admin-gate-authed">
      <div className="mt-[124px] border-b border-white/10 bg-[#141416] px-4 py-3 sm:mt-[152px] sm:px-8">
        <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-4">
            <span
              className="truncate text-[11px] font-extrabold uppercase tracking-widest text-[#b9b4a6]"
              data-testid="admin-bar-user"
            >
              Signed in as {email}
            </span>
            <nav className="flex flex-wrap items-center gap-3">
              <NavLink
                to="/admin/hours"
                className={({ isActive }) =>
                  `text-[11px] font-extrabold uppercase tracking-widest transition-colors ${
                    isActive ? "text-[#f8b818]" : "text-[#faf5ea]/75 hover:text-[#f8b818]"
                  }`
                }
                data-testid="admin-nav-hours"
              >
                Times
              </NavLink>
              <NavLink
                to="/admin/media"
                className={({ isActive }) =>
                  `text-[11px] font-extrabold uppercase tracking-widest transition-colors ${
                    isActive ? "text-[#f8b818]" : "text-[#faf5ea]/75 hover:text-[#f8b818]"
                  }`
                }
                data-testid="admin-nav-media"
              >
                Media
              </NavLink>
              <NavLink
                to="/admin/menu"
                className={({ isActive }) =>
                  `text-[11px] font-extrabold uppercase tracking-widest transition-colors ${
                    isActive ? "text-[#f8b818]" : "text-[#faf5ea]/75 hover:text-[#f8b818]"
                  }`
                }
                data-testid="admin-nav-menu"
              >
                Menu
              </NavLink>
              <NavLink
                to="/admin/analytics"
                className={({ isActive }) =>
                  `text-[11px] font-extrabold uppercase tracking-widest transition-colors ${
                    isActive ? "text-[#f8b818]" : "text-[#faf5ea]/75 hover:text-[#f8b818]"
                  }`
                }
                data-testid="admin-nav-analytics"
              >
                Stats
              </NavLink>
              <NavLink
                to="/admin/orders"
                className={({ isActive }) =>
                  `text-[11px] font-extrabold uppercase tracking-widest transition-colors ${
                    isActive ? "text-[#f8b818]" : "text-[#faf5ea]/75 hover:text-[#f8b818]"
                  }`
                }
                data-testid="admin-nav-orders"
              >
                Orders
              </NavLink>
            </nav>
          </div>
          <button
            onClick={logout}
            className="btn-outline shrink-0 !border-[#fff7e8]/40 !px-3 !py-1.5 !text-[10px] !text-[#fff7e8] hover:!border-[#f8b818] hover:!text-[#f8b818]"
            data-testid="admin-logout-btn"
          >
            <LogOut size={12} /> Log out
          </button>
        </div>
      </div>
      {children}
    </div>
  );
}
